import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { Link } from 'gatsby';

import colors from '../utils/colors';

const ResearchBody = styled.div`
  a {
    color: ${colors.accent1()};
    transition: all 0.5s;
    &:hover {
      background-color: ${colors.accent1(0.1)};
    }
  }
  margin-bottom: 60px;
  max-width: 750px;
  margin-left: auto;
  margin-right: auto;
`;

const SectionHeader = styled.h1`
  margin-top: 50px;
  margin-bottom: 20px;
  text-transform: uppercase;
  color: ${colors.base1()};
  font-size: 22px;
  letter-spacing: 2px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${colors.shade1()};
`;

const PaperTitle = styled.h2`
  margin: 0 0 6px 0;
  font-size: 19px;
  color: ${colors.base1()};
  line-height: 28px;
`;

const PaperMeta = styled.p`
  margin: 0 0 18px 0;
  font-size: 14px;
  color: ${colors.base3()};
  font-family: 'Open Sans', sans-serif;
`;

const Body = styled.p`
  font-size: 16px;
  line-height: 30px;
  color: ${colors.base2()};
  font-family: 'Open Sans', sans-serif;
  margin: 0 0 18px 0;
`;

const CiteHeader = styled.p`
  font-size: 14px;
  color: ${colors.base3()};
  font-family: 'Open Sans', sans-serif;
  margin: 0 0 18px 0;
`;

const PubList = styled.ol`
  padding-left: 22px;
  margin: 0;
`;

const PubItem = styled.li`
  font-size: 15px;
  line-height: 26px;
  color: ${colors.base2()};
  font-family: 'Open Sans', sans-serif;
  margin-bottom: 18px;
`;

const PubNote = styled.span`
  color: ${colors.accent2()};
  font-size: 13px;
`;

const TalkRow = styled.div`
  padding: 14px 0;
  border-top: 1px solid ${colors.shade1()};
  font-family: 'Open Sans', sans-serif;
  font-size: 15px;
  line-height: 26px;
  color: ${colors.base2()};

  &:last-child {
    border-bottom: 1px solid ${colors.shade1()};
  }
`;

const TalkVenue = styled.span`
  display: block;
  color: ${colors.base3()};
  font-size: 13px;
`;

const publications = [
  {
    authors: 'M. Abouali, K. Sharma, M. A. Obaidat, T. Saadawi.',
    title: 'Blockchain-Based Secure and Low Latency Health Record Exchange.',
    venue: 'IEEE ICETAS, 2024.',
    url: 'https://ieeexplore.ieee.org/abstract/document/11120131',
    note: null,
  },
  {
    authors: 'M. Abouali, K. Sharma, T. Saadawi.',
    title: 'Blockchain-Based Solution for Patient Health Records Sharing by Adopting Private Decentralized Storage.',
    venue: 'In Advanced AI and Internet of Health Things for Combating Pandemics, Springer, 2023, pp. 285-309. Book chapter.',
    url: 'https://link.springer.com/chapter/10.1007/978-3-031-28631-5_14',
    note: null,
  },
  {
    authors: 'M. Abouali, K. Sharma, T. Saadawi.',
    title: 'Access Delegation Framework for Private Decentralized Patient Health Records Sharing System Based on Blockchain.',
    venue: 'IEEE IEMCON, 2022, pp. 0007-0013.',
    url: 'https://ieeexplore.ieee.org/abstract/document/9946556',
    note: null,
  },
  {
    authors: 'M. Abouali, K. Sharma, T. Saadawi.',
    title: 'Patient Full Control over Secured Medical Records Transfer Framework Based on Blockchain.',
    venue: 'ICELTICs, 2022, pp. 43-48.',
    url: 'https://ieeexplore.ieee.org/abstract/document/9932113',
    note: null,
  },
  {
    authors: 'M. Abouali, K. Sharma, O. Ajayi, T. Saadawi.',
    title: 'Performance Evaluation of Secured Blockchain-Based Patient Health Records Sharing Framework.',
    venue: 'IEEE IEMTRONICS, 2022, pp. 1-7.',
    url: 'https://ieeexplore.ieee.org/abstract/document/9795759',
    note: '16 citations.',
  },
  {
    authors: 'M. Abouali, K. Sharma, O. Ajayi, T. Saadawi.',
    title: 'Blockchain Framework for Secured On-Demand Patient Health Records Sharing.',
    venue: 'IEEE UEMCON, 2021, pp. 0035-0040.',
    url: 'https://ieeexplore.ieee.org/abstract/document/9666482',
    note: 'Best Presentation Award. 28 citations.',
  },
];

const talks = [
  {
    title: 'A Comprehensive Review of Digital Solutions to Intimate Partner Violence (IPV).',
    authors: 'K. Sharma, B. Suh, M. Natarajan.',
    venue: 'American Society of Criminology, November 2025.',
    url: null,
  },
  {
    title: 'Developing a Digital Tool for Preventing Intimate Partner Violence: The Realtime Translational Impact.',
    authors: 'M. Natarajan, V. Lakshminarayanan, P. Rao, K. Sharma, T. Tabassum, B. Suh, V. H. Jeyaraman.',
    venue: 'American Society of Criminology, November 2025.',
    url: null,
  },
  {
    title: 'The Role of Digital Technology in Addressing Domestic Violence.',
    authors: 'K. Sharma, B. Suh, L. Begum, N. Ryu.',
    venue: 'Academy of Criminal Justice Sciences, Denver, March 2025.',
    url: 'https://s5.goeshow.com/acjs/annual/2025/conference_program_sessions.cfm?session_key=07C6873A-BED1-B492-6FC5-9B9C8C317CE0&session_date=Wednesday,%20Mar%2012,%202025',
  },
  {
    title: 'Landscape of Domestic Violence Mobile Apps.',
    authors: null,
    venue: 'IPV and Technology Workshop, John Jay College, New York, October 2024.',
    url: null,
  },
  {
    title: 'OwNFT: a self-hostable IPFS-backed data availability stack for Zora NFTs with a hardware wallet component.',
    authors: null,
    venue: 'ETHGlobal NFTHACK, 2021.',
    url: 'https://ethglobal.com/showcase/ownft-ru7x6',
  },
  {
    title: 'Decentralized Query Analytics Platform for U.S. Elections.',
    authors: null,
    venue: 'Grove School of Engineering Industry Day, New York, May 2021.',
    url: 'https://youtu.be/IPAhsXRjBo4',
  },
  {
    title: 'Predicting Type-2 Diabetes Using Machine Learning.',
    authors: null,
    venue: 'CCNY ORCA Symposium, New York, October 2020.',
    url: 'https://orca2020cohort.commons.gc.cuny.edu/2020/10/07/predicting-type-2-diabetes-using-machine-learning/',
  },
];

class ResearchPage extends React.Component {
  componentDidMount() {
    this.node.style.opacity = 0;
    window.requestAnimationFrame(() => {
      this.node.style.transition = 'opacity 1000ms ease-out';
      this.node.style.opacity = 1;
    });
  }

  render() {
    return (
      <ResearchBody ref={(node) => { this.node = node; }}>
        <Helmet>
          <title>Research - Kartikeya Sharma</title>
          <meta
            name="description"
            content="Working paper on class-aware adaptive clipping for differentially private learning, six peer-reviewed publications on threshold proxy re-encryption, and selected talks."
          />
        </Helmet>

        <SectionHeader>Working paper</SectionHeader>
        <PaperTitle>
          Class-Aware Adaptive Clipping for Differentially Private Learning on Imbalanced
          Clinical Data
        </PaperTitle>
        <PaperMeta>
          Sole author, July 2026. In preparation. Code:{' '}
          <a href="https://github.com/sha256rma/dp-diabetes-prediction" target="_blank" rel="noopener noreferrer">
            github.com/sha256rma/dp-diabetes-prediction
          </a>
        </PaperMeta>
        <Body>
          Uniform gradient clipping is not class-neutral. Median per-class gradient norms
          diverge by a factor of 7.75, so a single clipping bound systematically suppresses
          the minority class. The obvious remedy makes things worse in a way that is easy
          to miss: SMOTE generates synthetic points from real ones, placing each patient
          and their synthetic counterparts in a correlated group, so a guarantee stated at
          the record level silently becomes a group-level one and the accounting stops
          meaning what it claims.
        </Body>
        <Body>
          I introduced per-class adaptive bounds, which improve AUC by 0.0110 at matched
          epsilon and did not diverge across 3,780 runs. Across an 810-configuration grid
          the correlation between epsilon and AUC was +0.09, which is not the shape the
          privacy-utility tradeoff is usually assumed to have.
        </Body>
        <Body>
          Related writing:{' '}
          <Link to="/blog/smote-cancels-differential-privacy">
            How SMOTE Quietly Cancels Your Differential Privacy
          </Link>{' '}
          and{' '}
          <Link to="/blog/building-a-dp-mechanism">
            What It Takes To Build A Differentially Private Mechanism
          </Link>.
        </Body>

        <SectionHeader>Peer-reviewed publications</SectionHeader>
        <CiteHeader>
          49 citations,{' '}
          <a href="https://scholar.google.com/citations?user=Lei9wgwAAAAJ&hl=en" target="_blank" rel="noopener noreferrer">
            Google Scholar
          </a>
          , September 2026.
        </CiteHeader>
        <PubList>
          {publications.map((pub) => (
            <PubItem key={pub.url}>
              {pub.authors}{' '}
              <a href={pub.url} target="_blank" rel="noopener noreferrer">{pub.title}</a>{' '}
              {pub.venue}
              {pub.note ? <PubNote> {pub.note}</PubNote> : null}
            </PubItem>
          ))}
        </PubList>

        <SectionHeader>Standards contributions</SectionHeader>
        <Body>
          Formal comments on{' '}
          <a href="https://csrc.nist.gov/pubs/ir/8596/iprd" target="_blank" rel="noopener noreferrer">
            NIST IR 8596
          </a>
          , the draft Cybersecurity Framework Profile for Artificial Intelligence. Submitted
          for Tumeryk, Inc., January 2026. Twelve comments proposing that MITRE ATLAS
          mitigations (AML.M0020) be carried alongside NIST SP 800-53 references in the
          Defend column.
        </Body>

        <SectionHeader>Talks and conference presentations</SectionHeader>
        {talks.map((talk) => (
          <TalkRow key={talk.title}>
            {talk.url ? (
              <a href={talk.url} target="_blank" rel="noopener noreferrer">{talk.title}</a>
            ) : talk.title}
            <TalkVenue>
              {talk.authors ? `${talk.authors} ` : ''}{talk.venue}
            </TalkVenue>
          </TalkRow>
        ))}
      </ResearchBody>
    );
  }
}

export default ResearchPage;
